import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Menu,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCopy,
  IconKey,
  IconLock,
  IconMail,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { invitationsApi, InvitationView } from '@/services/invitations.api';
import { usersApi, UserView, CreatedMember } from '@/services/users.api';
import { getCurrentUserRole, getCurrentUserId } from '@/features/auth/currentUser';

const NAVY = '#415A91';

const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'EDITOR', label: 'Editor' },
  { value: 'REVIEWER', label: 'Reviewer' },
  { value: 'VIEWER', label: 'Viewer' },
];

const ROLE_COLORS: Record<string, string> = {
  ADMIN: 'grape',
  EDITOR: 'blue',
  REVIEWER: 'teal',
  VIEWER: 'gray',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'yellow',
  ACCEPTED: 'green',
  REVOKED: 'red',
  EXPIRED: 'gray',
};

function getMutationErrorMessage(error: any) {
  const responseMessage = error?.response?.data?.message;
  if (Array.isArray(responseMessage)) return responseMessage.join(' ');
  if (typeof responseMessage === 'string') return responseMessage;
  return error?.message || 'Something went wrong.';
}

function formatDate(value: string) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

export default function MembersPage() {
  const queryClient = useQueryClient();
  const role = getCurrentUserRole();
  const isAdmin = role === 'ADMIN';
  const currentUserId = getCurrentUserId();

  const [inviteModalOpened, setInviteModalOpened] = useState(false);
  const [form, setForm] = useState({ email: '', role: 'VIEWER' });
  const [revokeTarget, setRevokeTarget] = useState<InvitationView | null>(null);
  const [deleteUserTarget, setDeleteUserTarget] = useState<UserView | null>(null);

  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', role: 'VIEWER' });
  const [createdMember, setCreatedMember] = useState<CreatedMember | null>(null);

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
    enabled: isAdmin,
  });

  const { data: invitations = [], isLoading: invitationsLoading } = useQuery({
    queryKey: ['invitations'],
    queryFn: invitationsApi.list,
    enabled: isAdmin,
  });

  const createMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) => invitationsApi.create(email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      closeInviteModal();
      notifications.show({
        title: 'Invitation sent',
        message: 'The invitation has been sent successfully.',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: getMutationErrorMessage(error),
        color: 'red',
      });
    },
  });

  const createMemberMutation = useMutation({
    mutationFn: (payload: { name: string; email: string; role: string }) =>
      usersApi.create(payload as { name: string; email: string; role: UserView['role'] }),
    onSuccess: (member) => {
      setCreatedMember(member);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: getMutationErrorMessage(error),
        color: 'red',
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => invitationsApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      setRevokeTarget(null);
      notifications.show({
        title: 'Invitation revoked',
        message: 'The invitation has been revoked.',
        color: 'blue',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: getMutationErrorMessage(error),
        color: 'red',
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserView['role'] }) => usersApi.updateRole(id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({ title: 'Role updated', message: 'The member’s role has been updated.', color: 'green' });
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({ title: 'Error', message: getMutationErrorMessage(error), color: 'red' });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteUserTarget(null);
      notifications.show({ title: 'Member removed', message: 'The member has been removed.', color: 'blue' });
    },
    onError: (error: any) => {
      notifications.show({ title: 'Error', message: getMutationErrorMessage(error), color: 'red' });
    },
  });

  const openInviteModal = () => {
    setForm({ email: '', role: 'VIEWER' });
    setInviteModalOpened(true);
  };

  const closeInviteModal = () => {
    setInviteModalOpened(false);
  };

  const handleSubmit = () => {
    const email = form.email.trim();
    if (!email) return;
    createMutation.mutate({ email, role: form.role });
  };

  const openCreateModal = () => {
    setCreateForm({ name: '', email: '', role: 'VIEWER' });
    setCreatedMember(null);
    setCreateModalOpened(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpened(false);
    setCreatedMember(null);
  };

  const handleCreateSubmit = () => {
    const name = createForm.name.trim();
    const email = createForm.email.trim();
    if (!name || !email) return;
    createMemberMutation.mutate({ name, email, role: createForm.role });
  };

  if (!isAdmin) {
    return (
      <Box p="xl" px={32}>
        <Title order={2} c="#2A3140" mb="lg">Members &amp; Invitations</Title>
        <Alert icon={<IconLock size={16} />} color="gray" variant="light" title="Admins only">
          You need administrator access to manage members and invitations.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p="xl" px={32}>
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={2} c="#2A3140">Members &amp; Invitations</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Manage who has access to this workspace.
          </Text>
        </Box>
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <Button leftSection={<IconPlus size={16} />} style={{ backgroundColor: NAVY }} radius="md">
              Add Member
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconMail size={16} />} onClick={openInviteModal}>
              Send email invitation
            </Menu.Item>
            <Menu.Item leftSection={<IconKey size={16} />} onClick={openCreateModal}>
              Create with one-time password
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Members */}
      <Title order={4} c="#2A3140" mb="sm">Members</Title>
      <Paper radius="md" withBorder mb="xl" style={{ overflow: 'hidden' }}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th w={160}>Role</Table.Th>
              <Table.Th w={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    {usersLoading ? 'Loading members…' : 'No members yet.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              users.map((user) => {
                const isSelf = user.id === currentUserId;
                return (
                  <Table.Tr key={user.id}>
                    <Table.Td><Text size="sm" fw={500}>{user.name || '—'}</Text></Table.Td>
                    <Table.Td><Text size="sm" c="dimmed">{user.email}</Text></Table.Td>
                    <Table.Td>
                      {isSelf ? (
                        <Badge size="sm" color={ROLE_COLORS[user.role] || 'gray'} variant="light">{user.role}</Badge>
                      ) : (
                        <Select
                          size="xs"
                          w={130}
                          allowDeselect={false}
                          data={ROLE_OPTIONS}
                          value={user.role}
                          disabled={updateRoleMutation.isPending}
                          onChange={(value) => {
                            if (value && value !== user.role) {
                              updateRoleMutation.mutate({ id: user.id, role: value as UserView['role'] });
                            }
                          }}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      {isSelf ? (
                        <Tooltip label="You can’t remove your own account">
                          <ActionIcon variant="subtle" color="gray" size="sm" disabled>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Tooltip label="Remove member">
                          <ActionIcon variant="subtle" color="red" size="sm" onClick={() => setDeleteUserTarget(user)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Invitations */}
      <Title order={4} c="#2A3140" mb="sm">Invitations</Title>
      <Paper radius="md" withBorder style={{ overflow: 'hidden' }}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Email</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Sent</Table.Th>
              <Table.Th w={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {invitations.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    {invitationsLoading ? 'Loading invitations…' : 'No invitations yet.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              invitations.map((inv) => (
                <Table.Tr key={inv.id}>
                  <Table.Td><Text size="sm" fw={500}>{inv.email}</Text></Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={ROLE_COLORS[inv.role] || 'gray'} variant="light">{inv.role}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={STATUS_COLORS[inv.status] || 'gray'} variant="light">{inv.status}</Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{formatDate(inv.createdAt)}</Text></Table.Td>
                  <Table.Td>
                    {inv.status === 'PENDING' ? (
                      <Tooltip label="Revoke invitation">
                        <ActionIcon variant="subtle" color="red" size="sm" onClick={() => setRevokeTarget(inv)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    ) : (
                      <Text size="sm" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Invite Modal */}
      <Modal opened={inviteModalOpened} onClose={closeInviteModal} title="Invite Member" size="md">
        <Stack gap="md">
          <TextInput
            label="Email"
            placeholder="person@example.com"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
          />
          <Select
            label="Role"
            data={ROLE_OPTIONS}
            value={form.role}
            onChange={(value) => setForm({ ...form, role: value || 'VIEWER' })}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeInviteModal}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={!form.email.trim() || createMutation.isPending}
              loading={createMutation.isPending}
              style={{ backgroundColor: NAVY }}
            >
              Send Invitation
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create member with one-time password Modal */}
      <Modal
        opened={createModalOpened}
        onClose={closeCreateModal}
        title={createdMember ? 'Member created' : 'Create member'}
        size="md"
      >
        {createdMember ? (
          <Stack gap="md">
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              Copy this password now — it won&apos;t be shown again. Share it securely.{' '}
              {createdMember.name} will be asked to set a new password on first login.
            </Alert>
            <TextInput label="Email" value={createdMember.email} readOnly />
            <Group align="flex-end" gap="xs">
              <TextInput
                label="Temporary password"
                value={createdMember.temporaryPassword}
                readOnly
                style={{ flex: 1 }}
              />
              <CopyButton value={createdMember.temporaryPassword}>
                {({ copied, copy }) => (
                  <Button variant="light" onClick={copy} leftSection={<IconCopy size={16} />}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </CopyButton>
            </Group>
            <Group justify="flex-end">
              <Button style={{ backgroundColor: NAVY }} onClick={closeCreateModal}>
                Done
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="md">
            <TextInput
              label="Name"
              placeholder="Jane Doe"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.currentTarget.value })}
            />
            <TextInput
              label="Email"
              type="email"
              placeholder="person@example.com"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.currentTarget.value })}
            />
            <Select
              label="Role"
              data={ROLE_OPTIONS}
              value={createForm.role}
              onChange={(value) => setCreateForm({ ...createForm, role: value || 'VIEWER' })}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closeCreateModal}>Cancel</Button>
              <Button
                onClick={handleCreateSubmit}
                disabled={!createForm.name.trim() || !createForm.email.trim() || createMemberMutation.isPending}
                loading={createMemberMutation.isPending}
                style={{ backgroundColor: NAVY }}
              >
                Create member
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete member confirm Modal */}
      <Modal
        opened={!!deleteUserTarget}
        onClose={() => setDeleteUserTarget(null)}
        title="Remove member"
        size="sm"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Are you sure you want to remove{' '}
            <strong>{deleteUserTarget?.name || deleteUserTarget?.email}</strong> from this workspace?
            They will lose access immediately. This cannot be undone.
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteUserTarget(null)}>Cancel</Button>
            <Button
              color="red"
              loading={deleteUserMutation.isPending}
              onClick={() => deleteUserTarget && deleteUserMutation.mutate(deleteUserTarget.id)}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Revoke confirm Modal */}
      <Modal
        opened={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke invitation"
        size="sm"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Are you sure you want to revoke the invitation for{' '}
            <strong>{revokeTarget?.email}</strong>? This cannot be undone.
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRevokeTarget(null)}>Cancel</Button>
            <Button
              color="red"
              loading={revokeMutation.isPending}
              onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
            >
              Revoke
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
